import { useMutation, useQueryClient, QueryKey } from '@tanstack/react-query';
import { PermanentJobsService } from '../services';
import { IGetCandidateListResult } from '../types';
import { showErrorMessage } from '~/domain/utils';

interface IUseToggleShortlistCandidate {
  candidateId: string;
  isShortlisted: boolean;
}

export const useToggleShortlistCandidate = ({
  candidateId,
  isShortlisted,
}: IUseToggleShortlistCandidate) => {
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    void,
    { previousData: Array<[QueryKey, IGetCandidateListResult | undefined]> }
  >({
    mutationFn: () => {
      if (isShortlisted) {
        return PermanentJobsService.deleteShortlistCandidate({
          candidateId,
        });
      }

      return PermanentJobsService.createShortlistCandidate({ candidateId });
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['GET_CANDIDATE_LIST'] });

      const previousData = queryClient.getQueriesData<IGetCandidateListResult>({
        queryKey: ['GET_CANDIDATE_LIST'],
      });

      const newIsShortlistedValue = !isShortlisted;

      queryClient.setQueriesData<IGetCandidateListResult>(
        { queryKey: ['GET_CANDIDATE_LIST'] },
        (oldData) => {
          if (!oldData) return oldData;

          return {
            ...oldData,
            data: oldData.data.map((candidate) =>
              candidate.id === candidateId
                ? { ...candidate, isShortlisted: newIsShortlistedValue }
                : candidate,
            ),
          };
        },
      );

      return { previousData };
    },
    onError(error, _variables, context) {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }

      showErrorMessage({ error });
    },
  });
};
